import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, Printer } from "lucide-react";
import { Logo } from "../components/shared/Logo";
import { Button } from "../components/shared/Button";
import { getReport } from "../lib/api";

function fmtDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return iso.slice(0, 10);
    }
}

export default function Report() {
    const params = useParams();
    const [search] = useSearchParams();
    const responseId = params.response_id || params.id;
    const code = (search.get("code") || "").trim().toUpperCase();

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        if (!responseId || !code) {
            setError("Missing share code.");
            setLoading(false);
            return undefined;
        }
        (async () => {
            const res = await getReport({ responseId, code });
            if (!alive) return;
            if (res.ok) {
                setData(res.data);
                // Trigger the print dialog ~500ms after content paints.
                setTimeout(() => {
                    try {
                        window.print();
                    } catch {
                        // Browser blocked auto-print; user can hit the button.
                    }
                }, 600);
            } else if (res.forbidden || res.notFound) {
                setError("This snapshot link is invalid or has expired.");
            } else {
                setError("Couldn't load the snapshot. Try again in a moment.");
            }
            setLoading(false);
        })();
        return () => {
            alive = false;
        };
    }, [responseId, code]);

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center klareet-grain klareet-glow">
                <div className="inline-block w-8 h-8 rounded-full border-2 border-klareet-bor border-t-klareet-T animate-spin" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen grid place-items-center px-5">
                <div className="max-w-md text-center rounded-3xl border border-klareet-bor bg-klareet-card p-8">
                    <div className="text-klareet-RED text-2xl font-semibold tracking-tight">
                        Snapshot unavailable
                    </div>
                    <p className="mt-3 text-klareet-MUT">{error}</p>
                </div>
            </div>
        );
    }

    const score = data.score;
    const scoreColor =
        score >= 70 ? "#10B981" : score >= 50 ? "#F59E0B" : "#EF4444";

    return (
        <div className="report-root min-h-screen bg-klareet-NAVY text-klareet-TEXT">
            {/* Print button strip — hidden in print */}
            <div className="no-print sticky top-0 z-10 backdrop-blur bg-klareet-NAVY/85 border-b border-klareet-bor px-5 md:px-12 py-4 flex items-center justify-between">
                <Logo className="h-12 md:h-14 w-auto" />
                <Button
                    size="md"
                    onClick={() => window.print()}
                    data-testid="manual-print-btn"
                >
                    <Printer size={16} />
                    Print / Save as PDF
                </Button>
            </div>

            <article
                className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-16 space-y-10 print:py-8"
                data-testid="report-content"
            >
                <header className="space-y-3">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-T2">
                        Klareet · Career clarity snapshot
                    </div>
                    <h1 className="text-3xl md:text-5xl font-semibold tracking-[-0.02em] leading-[1.05]">
                        Your Klareet Snapshot
                    </h1>
                    <div className="text-sm text-klareet-MUT font-mono">
                        {fmtDate(data.created_at)} · Reference{" "}
                        <span className="text-klareet-TEXT">
                            {data.share_code}
                        </span>
                    </div>
                </header>

                <section className="rounded-3xl border border-klareet-bor bg-klareet-card p-7 print:break-inside-avoid">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-MUT">
                        Your archetype
                    </div>
                    <div className="mt-3 flex items-center gap-5">
                        <div className="text-5xl leading-none" aria-hidden>
                            {data.archetype.emoji}
                        </div>
                        <div className="min-w-0">
                            <h2
                                className="text-3xl md:text-4xl font-semibold tracking-tight"
                                style={{ color: data.archetype.color }}
                            >
                                {data.archetype.name}
                            </h2>
                        </div>
                    </div>
                    <p className="mt-4 text-lg italic text-klareet-TEXT/90 leading-relaxed">
                        “{data.archetype.line}”
                    </p>
                </section>

                <section className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center print:break-inside-avoid">
                    <div className="rounded-3xl border border-klareet-bor bg-klareet-card p-7 text-center w-full md:w-[220px]">
                        <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-MUT">
                            Clarity score
                        </div>
                        <div
                            className="mt-2 font-mono text-6xl font-semibold tabular-nums"
                            style={{ color: scoreColor }}
                        >
                            {score}
                        </div>
                        <div className="text-sm text-klareet-MUT font-mono">
                            / 100
                        </div>
                    </div>
                    <div className="rounded-3xl border border-klareet-T/40 bg-klareet-T/5 p-7">
                        <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-T2">
                            Strength signal
                        </div>
                        <p className="mt-3 text-lg text-klareet-TEXT leading-relaxed">
                            {data.strength}
                        </p>
                    </div>
                </section>

                <section className="rounded-3xl border border-klareet-bor bg-klareet-card p-7 print:break-inside-avoid">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-T2">
                        Week 1 action
                    </div>
                    <p className="mt-3 text-lg text-klareet-TEXT leading-relaxed">
                        {data.weekOneAction}
                    </p>
                    <p className="mt-3 text-sm text-klareet-MUT">
                        Weeks 2–12 unlock at launch alongside Klair, your
                        weekly accountability buddy.
                    </p>
                </section>

                <section className="rounded-3xl border border-klareet-bor bg-gradient-to-br from-klareet-CARD to-[#0B1120] p-7 print:break-inside-avoid">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-MUT">
                        Career clarity statement
                    </div>
                    <blockquote className="mt-3 text-2xl md:text-3xl font-semibold italic tracking-tight leading-snug">
                        “{data.clarityStatement}”
                    </blockquote>
                </section>

                {data.one_thing ? (
                    <section className="rounded-3xl border border-klareet-bor bg-klareet-card p-7 print:break-inside-avoid">
                        <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-MUT">
                            What career tools have never done for you
                        </div>
                        <p className="mt-3 text-base text-klareet-TEXT leading-relaxed whitespace-pre-line">
                            {data.one_thing}
                        </p>
                    </section>
                ) : null}

                <footer className="pt-8 text-center text-xs font-mono text-klareet-MUT border-t border-klareet-bor">
                    Generated by Klareet ·{" "}
                    <a
                        href="mailto:klair@klareet.com"
                        className="text-klareet-T2 hover:text-klareet-TEXT"
                    >
                        klair@klareet.com
                    </a>
                </footer>
            </article>

            <style>{`
                @media print {
                    body { background: white !important; color: #0a0f1e !important; }
                    .no-print { display: none !important; }
                    .report-root { background: white !important; color: #0a0f1e !important; }
                    .report-root *, .report-root h1, .report-root h2 { color: #0a0f1e !important; }
                    .report-root [class*="bg-klareet-card"],
                    .report-root [class*="bg-klareet-T/"],
                    .report-root [class*="from-klareet-"] { background: #ffffff !important; }
                    .report-root [class*="border-klareet"] { border-color: #d1d5db !important; }
                    .report-root .text-klareet-MUT, .report-root [class*="text-klareet-MUT"] { color: #475569 !important; }
                    .report-root .text-klareet-T2, .report-root [class*="text-klareet-T2"] { color: #0d9488 !important; }
                    a { color: #0d9488 !important; text-decoration: underline; }
                    @page { margin: 14mm; }
                }
            `}</style>
        </div>
    );
}

// Re-export Download icon to keep tree-shaking happy when other modules import the page directly.
export { Download };
